/** Matches `PitchCategoryImageIn.image_url` max_length in the API. */
export const API_IMAGE_DATA_URL_MAX_LENGTH = 2_000_000;

const DEFAULT_MAX_DATA_URL_LENGTH = API_IMAGE_DATA_URL_MAX_LENGTH - 10_000;
const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_MIN_DIMENSION = 480;

export type CompressImageOptions = {
  maxDataUrlLength?: number;
  maxDimension?: number;
  minDimension?: number;
  /** Always re-encode to JPEG (needed for ESC/POS logos; WebP is not supported by the printer encoder). */
  alwaysReencode?: boolean;
};

function isSvgFile(file: File): boolean {
  return file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
}

function svgPixelSize(svg: string, maxSide: number): { width: number; height: number } {
  const tag = svg.match(/<svg\b[^>]*>/i)?.[0] ?? "";
  const widthAttr = tag.match(/\bwidth\s*=\s*["']([\d.]+)/i);
  const heightAttr = tag.match(/\bheight\s*=\s*["']([\d.]+)/i);
  const viewBox = tag.match(/\bviewBox\s*=\s*["']\s*[\d.+-]+\s+[\d.+-]+\s+([\d.]+)\s+([\d.]+)/i);
  let width = widthAttr ? Number(widthAttr[1]) : Number.NaN;
  let height = heightAttr ? Number(heightAttr[1]) : Number.NaN;
  if (!(width > 0 && height > 0) && viewBox) {
    width = Number(viewBox[1]);
    height = Number(viewBox[2]);
  }
  if (!(width > 0 && height > 0)) {
    width = 512;
    height = 512;
  }
  const factor = Math.min(4, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
  };
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Das SVG konnte nicht gelesen werden."));
    image.src = url;
  });
}

/** Bon-Drucker brauchen ein Rasterbild. SVG wird dafür im Browser nach PNG gewandelt. */
async function rasterizeSvgFile(file: File, maxSide: number): Promise<File> {
  const text = await file.text();
  if (!/<svg[\s>/]/i.test(text)) {
    throw new Error("Die Datei ist kein gültiges SVG.");
  }
  const { width, height } = svgPixelSize(text, maxSide);
  const url = URL.createObjectURL(new Blob([text], { type: "image/svg+xml" }));
  try {
    const image = await loadImageElement(url);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Bildverarbeitung nicht möglich.");
    context.drawImage(image, 0, 0, width, height);
    const png = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Das SVG konnte nicht umgewandelt werden."));
      }, "image/png");
    });
    const baseName = file.name.replace(/\.svg$/i, "") || "logo";
    return new File([png], `${baseName}.png`, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Datei konnte nicht gelesen werden."));
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}

function canvasToJpegDataUrl(canvas: HTMLCanvasElement, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Bild konnte nicht komprimiert werden."));
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === "string") resolve(reader.result);
          else reject(new Error("Bild konnte nicht komprimiert werden."));
        };
        reader.onerror = () => reject(new Error("Bild konnte nicht komprimiert werden."));
        reader.readAsDataURL(blob);
      },
      "image/jpeg",
      quality,
    );
  });
}

function drawScaledImage(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  maxSide: number,
  fillWhiteBackground = false,
): HTMLCanvasElement {
  const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Bildverarbeitung nicht möglich.");
  if (fillWhiteBackground) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

/**
 * Returns a JPEG data URL suitable for API fields with a 2M character limit.
 * Large photos are downscaled and re-encoded in the browser before upload.
 */
export async function compressImageFileToDataUrl(
  file: File,
  options: CompressImageOptions = {},
): Promise<string> {
  const maxDataUrlLength = options.maxDataUrlLength ?? DEFAULT_MAX_DATA_URL_LENGTH;
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const minDimension = options.minDimension ?? DEFAULT_MIN_DIMENSION;

  const raw = await readFileAsDataUrl(file);
  if (!options.alwaysReencode && raw.length <= maxDataUrlLength) {
    return raw;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      "Das Bildformat wird nicht unterstützt oder die Datei ist beschädigt. Bitte verwenden Sie JPEG, PNG oder WebP.",
    );
  }

  try {
    let maxSide = maxDimension;
    while (maxSide >= minDimension) {
      const canvas = drawScaledImage(
        bitmap,
        bitmap.width,
        bitmap.height,
        maxSide,
        options.alwaysReencode === true,
      );
      for (let quality = 0.88; quality >= 0.45; quality -= 0.08) {
        const dataUrl = await canvasToJpegDataUrl(canvas, quality);
        if (dataUrl.length <= maxDataUrlLength) {
          return dataUrl;
        }
      }
      maxSide = Math.round(maxSide * 0.82);
    }
  } finally {
    bitmap.close();
  }

  throw new Error(
    "Das Foto ist auch nach Komprimierung zu groß. Bitte wählen Sie ein kleineres Bild oder eine niedrigere Auflösung.",
  );
}

/** Receipt logos are always re-encoded to JPEG for ESC/POS printer compatibility. */
export async function compressReceiptLogoToDataUrl(file: File): Promise<string> {
  const source = isSvgFile(file) ? await rasterizeSvgFile(file, 768) : file;
  return compressImageFileToDataUrl(source, {
    alwaysReencode: true,
    maxDimension: 768,
    minDimension: 128,
  });
}
